
import React, { useContext, useState, useEffect } from 'react';
import {
  Box, Typography, Button, IconButton, Alert, Stack, Paper,
  Container, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, useTheme, useMediaQuery
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { SettingsContext } from '../context/SettingsContext';

export default function FDTPage() {
  const {
    loudnessSettings,
    mclSettings,
    level,
    setLevel,
    hasSavedLoudness,
    hasSavedMCL
  } = useContext(SettingsContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [trialIndex, setTrialIndex] = useState(0);
  const [stimuliList, setStimuliList] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [isTrialStarted, setIsTrialStarted] = useState(false);
  const [trialStarted, setTrialStarted] = useState(false);

  const trials = 10;
  const allFiles = ['D1.wav', 'D2.wav', 'D3.wav', 'D4.wav', 'Target.wav'];
  const shuffle = arr => arr.slice().sort(() => Math.random() - 0.5);

  const generateStimuli = () => {
    return Array.from({ length: trials }, () => {
      const distractors = shuffle(allFiles.filter(f => f !== 'Target.wav')).slice(0, level);
      const items = ['Target.wav', ...distractors];
      return shuffle(items.map(name => ({
        type: name === 'Target.wav' ? 'target' : 'disturb',
        url: require(`../assets/pitchtone/${loudnessSettings.ear}/${loudnessSettings.pitch}Hz/${name}`)
      })));
    });
  };

  useEffect(() => {
    if (!loudnessSettings || !mclSettings) return;
    setTrialIndex(0);
    setCorrectCount(0);
    setAnswered(false);
    setCorrect(null);
    setFinished(false);
    setStimuliList(generateStimuli());
  }, [loudnessSettings, mclSettings, level]);

  useEffect(() => {
    if (finished && correctCount < 7) {
      setShowRetryModal(true);
    }
  }, [finished, correctCount]);

  useEffect(() => {
    setIsTrialStarted(false);
  }, [trialIndex]);

  const playAllStimuli = async () => {
    if (playing || finished) return;
    setPlaying(true);
    setIsTrialStarted(true);
    const seq = stimuliList[trialIndex] || [];

    for (let i = 0; i < seq.length; i++) {
      setPlayingIndex(i);
      const audio = new Audio(seq[i].url);
      audio.volume = mclSettings.mcl / 100;
      await new Promise(resolve => {
        audio.onended = resolve;
        audio.play();
      });
    }

    setPlaying(false);
    setPlayingIndex(null);
  };

  const handleSelect = (i) => {
    if (playing || answered || finished) return;
    setAnswered(true);
    const isCorrect = stimuliList[trialIndex][i].type === 'target';
    setCorrect(isCorrect);
    if (isCorrect) setCorrectCount(c => c + 1);
  };

  const handlePlayTarget = () => {
    if (playing || finished) return;
    setPlaying(true);
    const targetObj = stimuliList[trialIndex].find(o => o.type === 'target');
    const audio = new Audio(targetObj.url);
    audio.volume = mclSettings.mcl / 100;
    audio.play().finally(() => setPlaying(false));
  };

  const nextTrial = () => {
    if (trialIndex < trials - 1) {
      setTrialIndex(t => t + 1);
      setAnswered(false);
      setCorrect(null);
    } else {
      setFinished(true);
      if (correctCount >= 7 && level < 4) setLevel(level + 1);

    }
    playAllStimuli();
  };

  const handleRetry = () => {
    setShowRetryModal(false);
    setTrialIndex(0);
    setCorrectCount(0);
    setFinished(false);
    setAnswered(false);
    setCorrect(null);
    setStimuliList(generateStimuli());
  };

  if (!hasSavedLoudness || !hasSavedMCL || !loudnessSettings || !mclSettings) {
    return (
      <Container maxWidth="sm">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Please complete Pitch & Loudness and MCL settings first.
        </Alert>
      </Container>
    );
  }

  if (loudnessSettings.ear !== mclSettings.ear) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error" sx={{ mt: 4 }}>
          Please select the same ear for both Pitch & Loudness and MCL.
        </Alert>
      </Container>
    );
  }

  const current = stimuliList[trialIndex] || [];
  const targetPos = current.findIndex(o => o.type === 'target');

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
<Paper
  elevation={6}
  sx={{
    p: { xs: 2, sm: 4 },
    borderRadius: 4,
    width: '100%',
    maxWidth: 600,
    mx: 'auto',
    backgroundColor: '#f4f6f8',
  }}
>
  <Stack spacing={3}>

    {/* Ear & Level */}
    <Stack spacing={1}>
      <Typography variant="subtitle2"><strong>Ear:</strong> {loudnessSettings.ear}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2"><strong>Level:</strong></Typography>
        <Select
          value={level}
          onChange={(e) => setLevel(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 100 }}
        >
          {[1, 2, 3, 4].map((lvl) => (
            <MenuItem key={lvl} value={lvl}>Level {lvl}</MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>

    {/* Frequency & MCL */}
    <Stack spacing={1}>
      <Typography variant="subtitle2"><strong>Frequency:</strong> {loudnessSettings.pitch} Hz</Typography>
      <Typography variant="subtitle2"><strong>MCL:</strong> {mclSettings.mcl} dB</Typography>
    </Stack>

    {/* Trial Info */}
    <Typography variant="h6" align="center">
      Trial {trialIndex + 1} / {trials} | Score: {correctCount}/10
    </Typography>

    {/* Answer Cards */}
    <Stack
      direction="row"
      spacing={2}
      justifyContent="center"
      flexWrap="wrap"
      rowGap={2}
    >
      {current.map((_, i) => (
        <Box
          key={i}
          onClick={() => handleSelect(i)}
          sx={{
            width: { xs: 70, sm: 80 },
            height: { xs: 70, sm: 80 },
            bgcolor: answered
              ? (i === targetPos ? 'success.light' : 'error.light')
              : (i === playingIndex ? 'warning.light' : 'grey.300'),
            cursor: playing || answered ? 'default' : 'pointer',
            borderRadius: 2,
            boxShadow: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            transition: 'background-color 0.3s ease',
          }}
        >
          {i + 1}
        </Box>
      ))}
    </Stack>

    {/* Buttons */}
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      justifyContent="center"
      alignItems="center"
    >
      {!trialStarted && !answered && (
        <Button
          variant="contained"
          size="large"
          onClick={playAllStimuli}
          disabled={playing}
          fullWidth
        >
          {playing ? 'Playing...' : 'Play'}
        </Button>
      )}

      {answered && (
        <>
          <Button
            variant="outlined"
            onClick={handlePlayTarget}
            disabled={playing}
            fullWidth
          >
            Play Target
          </Button>
          <Button
            variant="contained"
            onClick={nextTrial}
            disabled={playing}
            fullWidth
          >
            Play
          </Button>
        </>
      )}
    </Stack>

    {/* Result Feedback */}
    {answered && (
      <Box textAlign="center">
        <Typography variant="h6" color={correct ? 'success.main' : 'error.main'}>
          {correct ? 'Correct!' : 'Try Again!'}
        </Typography>
        <IconButton color={correct ? 'success' : 'error'}>
          {correct ? <ThumbUpIcon fontSize="large" /> : <ThumbDownIcon fontSize="large" />}
        </IconButton>
      </Box>
    )}
  </Stack>
</Paper>




      <Dialog fullScreen={isMobile} open={showRetryModal}>
        <DialogTitle>Level {level} Failed</DialogTitle>
        <DialogContent>
          <Typography>You scored {correctCount}/10. You need at least 7 correct to advance.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRetry} variant="contained" size="large" fullWidth={isMobile}>
            Retry{level}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
